import { makeStyles, Theme, createStyles } from '@material-ui/core';
import { mainTheme } from '../../Theme';
import { ACTIVITY_COLUMN_MIN_WIDTH } from './enquiryFormDrawerConstants';

export const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            padding: '20px 15px',
            width: '100%',
            minWidth: '700px',
            maxWidth: '700px',
            transition: 'max-width 0.3s ease',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            position: 'relative',
            '& #enquiryForm': {
                flex: 1,
                display: 'flex',
                flexFlow: 'column',
            }
        },
        innerGridContainer: {
            marginTop: 0,
            marginBottom: 0,
        },
        hidden: {
            display: 'none',
        },
        heading: {
            fontSize: theme.typography.pxToRem(15),
            // fontWeight: theme.typography.fontWeightRegular,
            color: mainTheme.TemplateColor.Primary,
            display: 'flex',
            alignItems: 'center',
        },
        textField: {
            width: '100%',
        },
        buttonContainer: {
            display: 'flex',
            flexFlow: 'column',
            justifyContent: 'space-between',
            margin: '0 -8px',
            // paddingBottom: '20px'
        },
        button: {
            margin: theme.spacing(1),
        },
        chips: {
            display: 'flex',
            flexWrap: 'wrap',
        },
        chip: {
            marginLeft: '8px'
        },
        // Thin, sticky breadcrumb bar - same blue ConfigureTaskDialog.tsx's own dialogHeader
        // uses (taskflowBuilder/configureTaskDialog), per explicit request, but slimmed down
        // to a breadcrumb rather than carrying the contact details itself (those moved into
        // infoPanel below, per the reference screenshot). Sticky against the Drawer's own
        // Paper - the real scroll container here, since `root` below has no fixed height/
        // overflow of its own. Negative margin cancels `root`'s own 20px/15px padding so it
        // reaches the drawer's real edges (ConfigureTaskDialog's own header gets this for
        // free by sitting outside its Dialog's padded content instead).
        headerBar: {
            position: 'sticky',
            top: 0,
            zIndex: 5,
            backgroundColor: '#1565c0',
            margin: '-20px -15px 0 -15px',
            padding: theme.spacing(1, 2.5),
            // Explicit bug report: without this, headerBar's real height (padding + content,
            // ~36-40px) didn't actually match the 44px tabs' own `top: 44` assumes it docks
            // under - leaving a sliver gap while scrolling where content behind both sticky
            // bars was visible. Pinned to the same 44px both bars are meant to share.
            minHeight: 44,
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(0.75),
        },
        // Explicit request: the back link reads as a button - a soft pill (same translucent-white language as
        // the prev/next pill on the right) with hover / press / keyboard-focus states, and the arrow nudges
        // left on hover.
        headerBackLink: {
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            height: 30,
            padding: '0 14px 0 8px',
            borderRadius: 6,
            border: '1px solid rgba(255,255,255,0.28)',
            backgroundColor: 'rgba(255,255,255,0.12)',
            color: '#fff',
            fontWeight: 600,
            fontSize: 14,
            cursor: 'pointer',
            font: 'inherit',
            transition: 'background-color 0.15s ease, border-color 0.15s ease',
            '& .MuiSvgIcon-root': {
                transition: 'transform 0.15s ease',
            },
            '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.22)',
                borderColor: 'rgba(255,255,255,0.5)',
                '& .MuiSvgIcon-root': {
                    transform: 'translateX(-2px)',
                },
            },
            '&:active': {
                backgroundColor: 'rgba(255,255,255,0.3)',
            },
            '&:focus-visible': {
                outline: '2px solid #fff',
                outlineOffset: 2,
            },
            '@media (prefers-reduced-motion: reduce)': {
                transition: 'none',
                '& .MuiSvgIcon-root': {
                    transition: 'none',
                },
                '&:hover .MuiSvgIcon-root': {
                    transform: 'none',
                },
            },
        },
        headerBreadcrumbSeparator: {
            color: 'rgba(255,255,255,0.5)',
        },
        headerBreadcrumbCurrent: {
            color: '#fff',
            fontWeight: 600,
            fontSize: 14,
            opacity: 0.95,
            minWidth: 0,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        // Explicit change (enquiry prev/next navigation spec): marginLeft:'auto' moves to
        // headerNavGroup below (the first right-aligned element whenever listNavigation exists) -
        // this chip re-adds its own marginLeft:'auto' only when there's no nav group ahead of it
        // to have already claimed that space (applied conditionally in the JSX, Task 5).
        headerStatusChip: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            flexShrink: 0,
            // Explicit request: same 28px height and 12px/600 text as the prev/next pill and the tour
            // chip beside it - it used to size itself from its padding alone (~24px) at 11px.
            boxSizing: 'border-box',
            height: 28,
            border: '1px solid rgba(255,255,255,0.35)',
            borderRadius: 999,
            padding: theme.spacing(0, 1.25),
            fontSize: 12,
            fontWeight: 600,
            lineHeight: 1,
            color: '#fff',
            backgroundColor: 'rgba(255,255,255,0.1)',
            whiteSpace: 'nowrap',
        },
        // Explicit addition (enquiry prev/next navigation spec): wraps the prev/next
        // buttons+counter (Task 5) - first right-aligned element in headerBar when
        // listNavigation is defined, so it (not headerStatusChip) claims the row's leftover
        // space via marginLeft:'auto'.
        // Explicit request (approved "Enquiry Bar Navigation" sample): prev/counter/next as one
        // segmented pill - same border/fill as headerStatusChip so the bar's right side reads as
        // one visual family instead of three loose pieces. overflow:hidden clips the buttons'
        // hover fill to the pill's rounded ends; marginRight separates it from the status chip.
        headerNavGroup: {
            marginLeft: 'auto',
            display: 'inline-flex',
            alignItems: 'stretch',
            height: 28,
            border: '1px solid rgba(255,255,255,0.35)',
            borderRadius: 999,
            backgroundColor: 'rgba(255,255,255,0.1)',
            overflow: 'hidden',
            marginRight: theme.spacing(0.75),
            flexShrink: 0,
        },
        // Still used by the overflow ("More actions") button - the pill's own prev/next buttons
        // use headerNavPillButton below instead, so this is unchanged.
        headerNavButton: {
            color: '#fff',
            padding: 4,
            '&.Mui-disabled': {
                color: 'rgba(255,255,255,0.35)',
            },
        },
        // Explicit request: the form's tour button as an icon + "Take a tour" chip matching the
        // bar's other round controls - same 28px height, 999px radius, border and fill as the
        // prev/next pill (headerNavGroup) and the Active chip (headerStatusChip).
        // '&&' doubles the class so Button's own typography (0.875rem / 500) can't beat the
        // 12px/600 the other two bar chips use, whatever the stylesheet order.
        headerTourButton: {
            '&&': {
                boxSizing: 'border-box',
                height: 28,
                minWidth: 0,
                padding: '0 12px 0 10px',
                border: '1px solid rgba(255,255,255,0.35)',
                borderRadius: 999,
                backgroundColor: 'rgba(255,255,255,0.1)',
                color: '#fff',
                fontSize: 12,
                fontWeight: 600,
                lineHeight: 1,
                textTransform: 'none',
                whiteSpace: 'nowrap',
                flexShrink: 0,
            },
            '& .MuiButton-startIcon': {
                marginLeft: 0,
                marginRight: 6,
            },
            '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.18)',
            },
            '&.Mui-focusVisible': {
                outline: '2px solid #fff',
                outlineOffset: 2,
            },
        },
        // Wrapper span for each prev/next IconButton - MUI's Tooltip can't attach hover listeners
        // to a disabled button (no pointer events), so the Tooltip wraps this instead.
        headerNavPillButtonWrap: {
            display: 'flex',
        },
        // Disabled keeps its place in the pill at 40% (not the old near-invisible 35% on the
        // bare bar) so it still reads as a button that's switched off at the first/last result.
        headerNavPillButton: {
            width: 30,
            height: '100%',
            padding: 0,
            borderRadius: 0,
            color: '#fff',
            // Same arrow nudge as the back link: previous slides left, next slides right (not when disabled).
            '& .MuiSvgIcon-root': {
                transition: 'transform 0.15s ease',
            },
            '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.18)',
            },
            '&:hover:not(.Mui-disabled) .MuiSvgIcon-root': {
                transform: 'translateX(-2px)',
            },
            '&[aria-label="Next enquiry"]:hover:not(.Mui-disabled) .MuiSvgIcon-root': {
                transform: 'translateX(2px)',
            },
            '@media (prefers-reduced-motion: reduce)': {
                '& .MuiSvgIcon-root': {
                    transition: 'none',
                },
                '&:hover:not(.Mui-disabled) .MuiSvgIcon-root, &[aria-label="Next enquiry"]:hover:not(.Mui-disabled) .MuiSvgIcon-root': {
                    transform: 'none',
                },
            },
            '&.Mui-disabled': {
                color: '#fff',
                opacity: 0.4,
            },
            '&.Mui-focusVisible': {
                outline: '2px solid #fff',
                outlineOffset: -3,
            },
        },
        // tabular-nums + minWidth so "9 of 997" and "997 of 997" take the same space and the
        // pill doesn't change width as the user steps through results.
        headerNavCounter: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 96,
            padding: '0 10px',
            borderLeft: '1px solid rgba(255,255,255,0.22)',
            borderRight: '1px solid rgba(255,255,255,0.22)',
            color: '#fff',
            fontSize: 12,
            fontWeight: 600,
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
            cursor: 'default',
        },

        // ── Info panel (name/contact/dates/badges) - same rounded-card treatment as
        // rv-grid-container (App.css): 8px radius, soft shadow, 1px border.
        infoPanel: {
            backgroundColor: '#fff',
            borderRadius: 8,
            boxShadow: '0 1px 6px rgba(0,0,0,0.14)',
            border: '1px solid rgba(0,0,0,0.08)',
            marginTop: 20,
            marginBottom: 16,
            overflow: 'hidden',
        },
        // Explicit bug report: justifyContent:'space-between' across 3 fixed/capped-width
        // children (identity, the 900px-fixed journeyWidgetsRow, headerRight) left a large gap
        // sitting in the middle of the row on wide screens instead of giving that space to the
        // journey strip, which also meant the strip's own overflow-x:auto kicked in and hid Due/
        // Outcome pending even though there was plenty of room. flexWrap:'wrap' plus each child's
        // own explicit flex-basis (infoIdentity, EnquiryNextFollowUpWidget's wrapper,
        // EnquiryJourneyTimelineWidget, headerRight - see each one's own comment) replaces that:
        // the strip is now flex:'1 1 660px' so it grows into exactly that leftover space itself,
        // and headerRight's own marginLeft:'auto' keeps it pinned right with any remainder
        // landing between the strip and headerRight, not between the other blocks.
        infoPanelMain: {
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            columnGap: 24,
            rowGap: 16,
            // Explicit request: the header was too tall - a little less padding here, around the badges below,
            // and between the identity lines.
            padding: theme.spacing(1, 1.5),
        },
        // Explicit request: phone + mobile were being truncated ("0455 2...") because this column was a fixed
        // 320px. It now sizes to its content (never narrower than before, capped so a very long email still
        // truncates) so both numbers show in full; contactsContainer wraps them onto a second row if even
        // the cap is not enough.
        infoIdentity: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: theme.spacing(1.5),
            flex: '0 0 auto',
            minWidth: 320,
            maxWidth: 440,
        },
        // Explicit request: the Next Follow-up widget's own wrapper in wide layout - the widget's
        // own internal root class keeps its unrelated flex:'0 1 330px' (still used when it's
        // instead a direct infoPanelMain child in medium layout, below) since that's inert here
        // (the widget is a normal block inside this plain, non-flex wrapper div; this wrapper is
        // the actual infoPanelMain flex item).
        // Explicit request: Next Follow-up and the journey strip must be the same height.
        // infoPanelMain is align-items:'flex-start' (so identity/headerRight keep their natural
        // height), so each of these two opts in with alignSelf:'stretch' - both then take the
        // row's height. The widget inside fills this slot via its own height:'100%'.
        journeyNextFollowUpSlot: {
            flex: '0 0 260px',
            alignSelf: 'stretch',
        },
        // Explicit request: medium layout doesn't show the journey strip, so this widget centers
        // in the leftover row space between infoIdentity and headerRight instead of sitting
        // flush left - flex:1 claims that leftover space, justifyContent:'center' centers the
        // widget within it.
        journeyNextFollowUpCenteredSlot: {
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
        },
        // Explicit request: wide layout - Next Follow-up + the journey strip as one centered group.
        // flex:1 claims the leftover row space between infoIdentity and headerRight; the strip
        // (flex:'1 1 660px', capped at 880px by its own maxWidth) grows into it first, and whatever
        // is still left over is split evenly either side by justifyContent:'center'. alignSelf:'stretch'
        // keeps the row-height behaviour both widgets already rely on (infoPanelMain is
        // align-items:'flex-start'), and the 24px gap matches infoPanelMain's own columnGap.
        journeyWidgetsCenteredGroup: {
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignSelf: 'stretch',
            columnGap: 24,
            minWidth: 0,
        },
        infoAvatar: {
            width: 48,
            height: 48,
            borderRadius: '50%',
            backgroundColor: '#e3f2fd',
            color: '#1565c0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: 16,
            flexShrink: 0,
        },
        infoAvatarEmpty: {
            backgroundColor: '#f0f1f3',
            color: mainTheme.TextColor.Secondary,
        },
        infoIdentityPlaceholder: {
            fontSize: '13px',
            fontStyle: 'italic',
            color: mainTheme.TextColor.Secondary,
            margin: 0,
            paddingTop: 6,
        },
        headerDetails: {
            minWidth: 0,
            // Explicit request: a very long name used to wrap and run over the widgets next to it - it (and the
            // organisation) now stay on one line and end with an ellipsis; the full text is in the title tooltip.
            '& .user': {
                fontSize: '19px',
                fontWeight: 700,
                marginBottom: '2px',
                color: mainTheme.TextColor.Primary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            },
            '& .company': {
                fontSize: '13px',
                fontWeight: 400,
                marginBottom: '2px',
                color: mainTheme.TextColor.Secondary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            },
            '& .phone': {
                color: mainTheme.TemplateColor.Primary,
                fontSize: '13px',
                marginBottom: '2px',
                '& a': {
                    color: 'inherit'
                }
            },
            '& .email': {
                color: mainTheme.TemplateColor.Primary,
                fontSize: '13px',
                marginBottom: '2px',
                '& a': {
                    color: 'inherit'
                }
            },
            '& .common': {
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                fontSize: '12px',
                color: mainTheme.TextColor.Secondary,
                marginBottom: '4px',
                '& strong': {
                    color: mainTheme.TextColor.Primary,
                    fontWeight: 700,
                },
                // Explicit request: the assignee's name uses the same indigo pill as the Activity timeline
                // (HistoryActivity.tsx's '.assigneeChip').
                '& .assigneeChip': {
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '1px 8px',
                    borderRadius: 999,
                    fontWeight: 700,
                    fontSize: '12px',
                    backgroundColor: 'var(--rv-accent-indigo-bg)',
                    color: 'var(--rv-accent-indigo)',
                },
            }
        },
        headerLeft: {
            alignItems: 'flex-start'
        },
        // New enquiry: the identity lines are always laid out (name, email, phone), hidden until something is typed, with the placeholder text over them, so the
        // header keeps one height from the first keystroke. infoIdentityFadedOut hides either layer; the opacity change is the cross-fade.
        infoIdentityStack: {
            position: 'relative',
        },
        infoIdentityContent: {
            transition: 'opacity 0.2s ease',
        },
        infoIdentityFadedOut: {
            opacity: 0,
            pointerEvents: 'none',
        },
        infoIdentityPlaceholderOverlay: {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            transition: 'opacity 0.2s ease',
        },
        // Explicit bug report: the two Journey widgets (EnquiryNextFollowUpWidget,
        // EnquiryJourneyTimelineWidget) used to share one fixed flex:'0 0 900px' wrapper so
        // infoPanelMain's own justify-content:'space-between' could treat them as one block -
        // now each is its own direct infoPanelMain flex child with its own flex-basis
        // (journeyNextFollowUpSlot above, EnquiryJourneyTimelineWidget's own root class), so the
        // strip can grow independently into whatever space infoPanelMain's flexWrap actually
        // gives it instead of being capped at a fixed total.
        headerRight: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            flexShrink: 0,
            // Explicit request: stays right-aligned, soaking up any leftover row space itself
            // rather than infoPanelMain's old justify-content:'space-between' splitting that
            // space between every gap in the row.
            marginLeft: 'auto',
        },
        // Folded into headerRight's own "common" stack (Created/Assigned/Follow-up) instead
        // of a separate full-width banner row - explicit request, the banner cost too much
        // vertical space. Chip-styled (not plain text) so overdue vs still-due reads at a
        // glance - same red/amber pairing ConfigureTaskDialog.tsx's own gateFlag uses.
        followUpChip: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            marginTop: 2,
            padding: theme.spacing(0.25, 1),
            borderRadius: 999,
            fontSize: '11px',
            fontWeight: 700,
            '& svg': {
                fontSize: '13px',
            },
        },
        followUpChipOverdue: {
            backgroundColor: '#fdecea',
            color: '#b71c1c',
        },
        followUpChipDue: {
            backgroundColor: '#fff6e9',
            color: '#a15f04',
        },
        // Explicit request: this chip now shows "Open for N days" instead of the next
        // follow-up's own due/overdue state (that's covered by the new Next Follow-up header
        // widget instead, so showing it here too was redundant) - a neutral blue tint rather
        // than the red/amber urgency pairing above, since "days open" isn't an urgency signal.
        followUpChipNeutral: {
            backgroundColor: 'var(--rv-accent-blue-bg)',
            color: 'var(--rv-accent-blue)',
        },
        // Check Duplicates button - explicit request for a clearer style, a loading state,
        // and a visible result. Result used to be a separate chip; now the button's own
        // text/icon/colour swap to the result for a few seconds instead (see
        // isShowingDuplicateResult), so checkDuplicatesButtonOk/Warning just recolour this
        // same button rather than being their own element.
        checkDuplicatesButton: {
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '13px',
            borderRadius: 999,
            borderColor: mainTheme.CommonColor.LightGrey2,
            backgroundColor: '#fff',
            // Explicit request: make this read as a pressable button rather than a flat
            // outline - same raised-card hover treatment CLAUDE.md's Card Grids convention
            // uses (box-shadow/transform, disabled under prefers-reduced-motion).
            boxShadow: '0 1px 2px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.1)',
            transition: 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.15s ease, transform 0.15s ease',
            '&:hover': {
                boxShadow: '0 3px 8px rgba(0,0,0,0.16)',
                transform: 'translateY(-1px)',
            },
            '&:active': {
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                transform: 'translateY(0)',
            },
            '@media (prefers-reduced-motion: reduce)': {
                '&:hover': {
                    transform: 'none',
                },
            },
        },
        checkDuplicatesButtonOk: {
            backgroundColor: 'var(--rv-accent-green-bg)',
            borderColor: 'var(--rv-accent-green-bg)',
            color: 'var(--rv-accent-green)',
            '&:hover': {
                backgroundColor: 'var(--rv-accent-green-bg)',
            },
        },
        checkDuplicatesButtonWarning: {
            backgroundColor: '#fdecea',
            borderColor: '#fdecea',
            color: '#b71c1c',
            '&:hover': {
                backgroundColor: '#fdecea',
            },
        },
        checkDuplicatesButtonContent: {
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            '& svg': {
                fontSize: '15px',
            },
        },
        // Explicit request: same raised-button hover treatment as checkDuplicatesButton -
        // 8px radius rather than a pill, since this button spans the row's full width.
        uploadMatterDocumentButton: {
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '13px',
            borderRadius: 8,
            borderColor: mainTheme.CommonColor.LightGrey2,
            backgroundColor: '#fff',
            boxShadow: '0 1px 2px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.1)',
            transition: 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.15s ease, transform 0.15s ease',
            '&:hover': {
                boxShadow: '0 3px 8px rgba(0,0,0,0.16)',
                transform: 'translateY(-1px)',
            },
            '&:active': {
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                transform: 'translateY(0)',
            },
            '@media (prefers-reduced-motion: reduce)': {
                '&:hover': {
                    transform: 'none',
                },
            },
        },
        uploadMatterDocumentButtonContent: {
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            '& svg': {
                fontSize: '17px',
            },
        },
        // Explicit request: the always-visible explanation above the Upload Documents button (it used to be a hover
        // tooltip only).
        uploadMatterDocumentPanel: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            boxSizing: 'border-box',
            padding: theme.spacing(1.5, 2),
            border: '1px solid rgba(0,0,0,0.08)',
            borderRadius: 8,
            backgroundColor: '#fafbfd',
        },
        // Explicit request: the Matter tab's "details are still syncing" message is the same soft notice row as the Link Matter card in the history
        // (and Auto Follow Up Removed), not the old yellow note.
        syncNotice: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            margin: theme.spacing(0, 0, 2),
            padding: theme.spacing(1, 1.5),
            borderRadius: 8,
            backgroundColor: theme.palette.grey[100],
            color: theme.palette.text.secondary,
            fontSize: 13,
            lineHeight: 1.4,
        },
        syncNoticeIcon: {
            flexShrink: 0,
            fontSize: 18,
            color: 'var(--rv-accent-indigo)',
        },
        creationWarningAlert: {
            flexShrink: 0,
            marginBottom: theme.spacing(2),
        },
        uploadMatterDocumentHelper: {
            margin: '0 0 12px',
            fontSize: 12.5,
            lineHeight: 1.5,
            color: mainTheme.TextColor.Secondary,
        },
        badgeRow: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: theme.spacing(1),
            padding: theme.spacing(0.5, 1.5),
            borderTop: '1px solid rgba(0,0,0,0.06)',
        },
        // the badges themselves, as wide as they are (badgeRow runs the full width and carries the divider line): what the form tour points at
        badgeRowContent: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: theme.spacing(1),
            width: 'fit-content',
            maxWidth: '100%',
        },
        infoBadge: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: theme.spacing(0.5, 1.5),
            borderRadius: 999,
            border: `1px solid ${theme.palette.grey[300]}`,
        },
        infoBadgeLabel: {
            fontSize: '10.5px',
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: mainTheme.TextColor.Secondary,
        },
        // Explicit request: the header's Quality / Likelihood badges report their own save - the label swaps to
        // Saving… / Saved ✓ / Not saved in place (EnquiryRatingBadge). Every possible text sits in the same grid cell, the
        // hidden ones kept only for sizing, so the pill is as wide as the longest one and nothing around it moves.
        // The swap uses a 4px rise rather than AnimatedItem's 10px, which would overshoot a pill this short.
        infoBadgeLabelStack: {
            display: 'inline-grid',
        },
        infoBadgeLabelText: {
            gridArea: '1 / 1',
            whiteSpace: 'nowrap',
        },
        infoBadgeLabelHidden: {
            visibility: 'hidden',
        },
        infoBadgeLabelSwap: {
            animation: '$infoBadgeLabelSwapIn 0.3s ease-out both',
            '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
            },
        },
        '@keyframes infoBadgeLabelSwapIn': {
            from: { opacity: 0, transform: 'translateY(4px)' },
            to: { opacity: 1, transform: 'translateY(0)' },
        },
        infoBadgeLabelSaving: {
            color: mainTheme.TextColor.Tertiary,
        },
        infoBadgeLabelSaved: {
            color: 'var(--rv-accent-green)',
        },
        infoBadgeLabelFailed: {
            color: 'var(--rv-accent-red)',
        },
        // Explicit request: the stars themselves ripple on a click (A / B are the same animation under two names so a
        // second click while the first is still running restarts it) and dim while the save is in flight.
        ratingStarFilled: {
            transition: 'opacity 0.3s ease',
        },
        ratingSaving: {
            '& $ratingStarFilled': {
                opacity: 0.55,
            },
        },
        ratingPopA: {
            '& $ratingStarFilled': {
                animation: '$ratingStarPopA 0.35s ease-out both',
            },
        },
        ratingPopB: {
            '& $ratingStarFilled': {
                animation: '$ratingStarPopB 0.35s ease-out both',
            },
        },
        '@keyframes ratingStarPopA': {
            '0%': { transform: 'scale(1)' },
            '45%': { transform: 'scale(1.3)' },
            '100%': { transform: 'scale(1)' },
        },
        '@keyframes ratingStarPopB': {
            '0%': { transform: 'scale(1)' },
            '45%': { transform: 'scale(1.3)' },
            '100%': { transform: 'scale(1)' },
        },
        '@media (prefers-reduced-motion: reduce)': {
            ratingPopA: { '& $ratingStarFilled': { animation: 'none' } },
            ratingPopB: { '& $ratingStarFilled': { animation: 'none' } },
        },
        infoBadgeValue: {
            fontSize: '12px',
            fontWeight: 700,
            color: mainTheme.TextColor.Primary,
        },
        infoBadgeIcon: {
            fontSize: '13px',
            color: mainTheme.TextColor.Secondary,
        },
        statusDotActive: {
            fontSize: '10px !important',
            color: mainTheme.TemplateColor.Secondary,
        },
        statusDotClosed: {
            fontSize: '10px !important',
            color: mainTheme.TextColor.Tertiary,
        },

        // ── Sticky footer action row - separate wrapper around the existing
        // buttonContainer (see actionButton's own render) rather than folding this
        // treatment into buttonContainer itself, since that class's own `margin: '0 -8px'`
        // serves a different, smaller-scale purpose (aligning its own Button children)
        // unrelated to bleeding this bar out to the drawer's real edges.
        stickyFooterBar: {
            position: 'sticky',
            bottom: 0,
            zIndex: 5,
            backgroundColor: '#fff',
            // Explicit request: a full border (not just the top edge) so the bar reads as
            // its own clearly framed panel rather than the top divider it had before.
            border: '1px solid rgba(0,0,0,0.1)',
            margin: '20px -15px -20px -15px',
            padding: theme.spacing(1, 2.5),
            boxShadow: '0 -2px 8px rgba(0,0,0,0.06)',
        },
        customFieldGrid: {
            paddingTop: '0px !important',
        },
        // Explicit request: slightly more vertical breathing room between this drawer's own
        // outlined+small fields, confirmed first on Contact Details before rolling out further -
        // customFieldGrid above is shared across ~130 other call sites in this file, so this adds
        // a touch of extra row gap on top of that (combined via clsx) rather than changing the
        // shared class or a Grid's own spacing prop (which would also widen the horizontal gap
        // between columns, not just vertical).
        fieldRowGap: {
            paddingBottom: '8px !important',
        },
        // Explicit bug report: "Assigned To"/"Assigned By" (UserSelector, backed by
        // mui-downshift) render noticeably taller than every sibling field in the same Grid
        // row (Follow Up Due, Reminder, Date Assigned), which all use variant="outlined"
        // size="small". mui-downshift has no size/margin/classes prop at all (confirmed from
        // its own source), so there's no way to ask it for MUI's dense sizing - this instead
        // targets the MUI classes it renders directly, matching MUI v4's own dense values
        // (OutlinedInput.js inputMarginDense: 10.5px vertical padding; IconButton.js
        // sizeSmall: 3px padding, 18px icon) so the row lines up with its "small" neighbours.
        userSelectorDense: {
            '& .MuiOutlinedInput-input': {
                padding: '10.5px 14px',
            },
            '& .MuiInputAdornment-positionEnd .MuiIconButton-root': {
                padding: 3,
            },
            '& .MuiInputAdornment-positionEnd .MuiSvgIcon-root': {
                fontSize: 18,
            },
        },
        expandablePanelDetails: {
            paddingBottom: '15px',
            // Explicit bug report: a shrunk field label (one with a value, floated up small
            // above its input) sits close to this container's own top edge - MUI's default
            // AccordionDetails top padding (8px) leaves little clearance, and
            // Collapse's own overflow:hidden during the open/close transition clips into it
            // mid-animation, settling fine only once fully open. More top clearance keeps
            // the shrunk label inside the clipping bounds throughout the transition instead
            // of just at rest.
            paddingTop: '20px',
        },
        // Explicit bug report: MUI v4's AccordionDetails is `display: flex` (row direction) by
        // default, meant for panels with multiple side-by-side columns. Every OTHER panel in
        // this drawer only ever gives it a single child (one Grid container), so that default
        // is invisible there - but the Follow Up panel's own footer (followUpPanelActions,
        // rendered as a second, sibling child alongside the Grid container once it moved
        // outside that Grid for its own edge-to-edge background) became a second flex-row item
        // instead of stacking below the fields - default align-items:stretch then stretched it
        // to the panel's full height as a narrow column instead of a normal footer bar. Scoped
        // to just this one panel (not a change to the shared expandablePanelDetails class, which
        // every other panel also uses) since it's the only one with more than one direct child.
        expandablePanelDetailsColumn: {
            flexDirection: 'column',
        },
        expansionPanelSummary: {
            minHeight: 'auto !important',
            '& .MuiAccordionSummary-content': {
                justifyContent: 'space-between',
                margin: '12px 0',
            },
            // Explicit request: the same header/body divider activityCard's own
            // cardHeaderRowWithBody draws (HistoryActivity.tsx) - only while expanded, since a
            // collapsed panel has no body underneath to divide from.
            '&.Mui-expanded': {
                borderBottom: '1px solid #E2E7EE',
            },
        },
        infoContainer: {
            display: 'flex',
            margin: '0 -10px',
            paddingLeft: '10px',
            justifyContent: 'flex-end',
            '& .col': {
                padding: '0 10px',
                display: 'flex',
                alignItems: 'center',
                '& .label': {
                    marginRight: '10px',
                },
                '& .files': {
                    fontWeight: 600,
                },
                '& .ellipsis': {
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    width: '250px',
                    textAlign: 'right',
                },
                '& .value': {
                    maxWidth: '400px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }
            },
        },
        dropZone: {
            minHeight: 'fit-content !important',
            '& .MuiDropzoneArea-root': {
                minHeight: 'fit-content !important'
            }
        },
        formControl: {
            width: '100%',
        },
        hideDate: {
            display: 'none'
        },
        expansionPanel: {
            // 8px at rest, matching how tightly-stacked collapsed panel lists are usually
            // spaced - 16px only kicks back in while a panel is actually open, via MUI's own
            // .Mui-expanded class below (its own default behaviour, just not wired up here
            // before now - this file previously applied the "expanded" value unconditionally,
            // which adds up across this tab's ~14 panels when everything's collapsed).
            marginBottom: '8px',
            // Tried easing this alongside MUI's own height transition (matched duration) to
            // chase a label-clipping bug - turned out to be the wrong fix (expandablePanelDetails'
            // paddingTop below was the real cause) and this transition, animating on its own
            // schedule against MUI's own height animation, was itself making open/close read
            // as a jarring "jump" - explicit bug report. Back to an instant snap.
            '&.Mui-expanded': {
                marginBottom: '16px',
            },
            // MUI's own Accordion only applies .MuiPaper-rounded's border-radius to the
            // first/last panel in a DOM-adjacent group (built for flush-stacked accordions,
            // rounding just the group's own outer corners) - since these panels sit spaced
            // apart (this 16px marginBottom) rather than touching, most of them rendered with
            // 0px, not even the 4px default. Same 4px ConfigureTaskDialog.tsx's own
            // .MuiPaper-rounded dialog uses (and TaskFlow/marketing automation's own panels) -
            // the app-wide panel-container radius, applied uniformly here instead of leaving it
            // to MUI's group-relative default. activityCard (HistoryActivity.tsx) matches this
            // same 4px now too, so Details/Activity panels and TaskFlow/marketing automation's
            // own dialogs all agree.
            borderRadius: '4px',
            // MUI's own Accordion adds a 1px :before divider above each panel, meant
            // for zero-gap stacked accordions (and suppressed only while that specific panel
            // is expanded, matching MUI's default rule for hiding it against an expanded
            // neighbour) - explicit bug report/screenshot: these panels already have their
            // own 16px marginBottom + shadow, so the extra hairline had nothing to do here
            // and only read as a stray line that vanished on expand.
            '&:before': {
                display: 'none',
            },
            // Explicit request: Details-tab panels to match the Activity tab's own cards
            // (activityCard in HistoryActivity.tsx) - same hairline border + faint shadow at
            // rest, deeper shadow/border-color on hover - instead of MUI's own default Paper
            // elevation shadow (heavier/darker than this app's own card treatment). Purely
            // cosmetic - no change to expanded/collapsed behaviour, which stays driven by the
            // same expanded/onChange props each panel already passes to <Accordion>.
            border: '1px solid #E2E7EE',
            boxShadow: '0 1px 2px rgba(20,30,50,.05)',
            transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
            '&:hover': {
                borderColor: '#CDD4DF',
                boxShadow: '0 8px 18px -10px rgba(20,30,50,.28)',
            },
            '@media (prefers-reduced-motion: reduce)': {
                '&:hover': {
                    boxShadow: '0 1px 2px rgba(20,30,50,.05)',
                },
            },
        },
        expansionPanel2: {
            marginBottom: '16px',
            boxShadow: '0px 3px 3px -2px rgba(81,115,255, 0.6), 0px 3px 4px 0px rgba(81,115,255,0.14), 0px 1px 8px 0px rgba(81,115,255,0.7)'
        },
        loaderWrapper: {
            position: 'absolute',
            top: 0,
            bottom: 0,
            right: 0,
            left: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        // Sticky under the blue headerBar (docks at its own 44px height) once the info panel
        // scrolls past - approved via the standalone tab-bar preview artifact before this was
        // built. MUI's own sliding indicator is hidden in favour of a static per-Tab ::after
        // bar (tabItem's own &.Mui-selected rule below), matching that preview exactly rather
        // than the slide-between-tabs animation MuiTabs-indicator gives by default.
        tabs: {
            position: 'sticky',
            top: 44,
            zIndex: 2,
            // Negative margin cancels this Grid item's own spacing={3} top padding (12px)
            // - explicit request to close the gap between infoPanel and the tabs. Only
            // affects the tabs' natural (pre-stick) position - harmless once stuck.
            marginTop: -12,
            marginBottom: 10,
            minHeight: 44,
            height: 44,
            padding: '0 11px',
            backgroundColor: '#fff',
            borderBottom: `1px solid ${mainTheme.CommonColor.LightGrey2}`,
            '& .MuiTabs-flexContainer': {
                gap: 2,
                height: '100%',
            },
            '& .MuiTabs-indicator': {
                display: 'none',
            },
        },
        tabItem: {
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '13px',
            minWidth: 'auto',
            minHeight: 44,
            padding: '0 14px',
            color: '#667085',
            borderRadius: '6px 6px 0 0',
            position: 'relative',
            transition: 'color 0.12s ease, background-color 0.12s ease',
            // MuiTab-wrapper defaults to flexDirection:'column' (built for icon-above-label
            // tabs) - explicit bug report that the Activity count badge was wrapping onto its
            // own line underneath the label instead of sitting beside it. Row + gap replaces
            // the manual marginLeft on tabCount below for that spacing instead.
            '& .MuiTab-wrapper': {
                flexDirection: 'row',
                gap: 4,
            },
            '&::after': {
                content: '""',
                position: 'absolute',
                left: 10,
                right: 10,
                bottom: 0,
                height: 3,
                borderRadius: '3px 3px 0 0',
                backgroundColor: 'transparent',
                transition: 'background-color 0.15s ease',
            },
            '&:hover': {
                color: '#344054',
                backgroundColor: 'rgba(21, 101, 192, 0.07)',
            },
            '&.Mui-selected': {
                color: '#1565c0',
                fontWeight: 600,
            },
            '&.Mui-selected::after': {
                backgroundColor: '#1565c0',
            },
        },
        tabCount: {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            minWidth: 20,
            height: 18,
            padding: '0 6px',
            borderRadius: 999,
            backgroundColor: '#eef2f7',
            color: '#475467',
            fontSize: '10.5px',
            fontWeight: 700,
        },
        tabCountSelected: {
            backgroundColor: 'rgba(21, 101, 192, 0.12)',
            color: '#1565c0',
        },
        historyActivityContainer: {
            flex: 1,
            position: 'relative',
            flexFlow: 'column',
            minHeight: 250,
        },
        // Explicit bug report: the Activity tab's sticky chip filter row let scrolled content
        // show through behind it. Root cause - this class is combined onto HistoryActivity's
        // own root div (via its className prop), which has its own `position: 'relative'`
        // (HistoryActivity.tsx's useStyles). Both rules target `position` on the same element;
        // since HistoryActivity's own sheet mounts after this one (child renders after parent),
        // its `relative` wins the cascade, so this div never actually absolutely-fills
        // historyActivityContainer - breaking the bounded height overflowY:'auto' needs to be a
        // correctly-sized scroll viewport, which is what made the sticky row's masking
        // unreliable. !important makes this call site's fill-the-parent intent win regardless
        // of sheet insertion order, without touching HistoryActivity's own default (still relied
        // on as-is by LeadsDetailPage.tsx, which doesn't pass this className at all).
        historyActivityWrapper: {
            // tslint:disable-next-line: no-any
            position: 'absolute !important' as any,
            top: 0,
            right: 0,
            left: 0,
            bottom: 0,
            // Explicit bug report: the scrollbar drew over the right-aligned times ("in 2 hours") on
            // overlay-scrollbar platforms (macOS), where it takes no layout space of its own.
            paddingRight: 12,
        },
        activityTab: {
            flex: 1,
            flexFlow: 'column',
        },
        // Wraps {detailsTab()}{activityTab()}{matterDetailsTab()}{customFields} - narrow
        // layout keeps the flex-column chain those 4 pieces already relied on from their old
        // parent Grid item (so the sticky footer below still fills the remaining height
        // exactly as before); wide layout (isWideLayout, see EnquiryFormDrawer's own const)
        // switches it to the 3-column grid those same 4 pieces now place themselves into via
        // their own gridArea styles.
        tabsContentArea: {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
        },
        tabsContentAreaWide: {
            flex: 1,
            display: 'grid',
            // Each column kept at the same 700px the single-column drawer's own root already
            // uses (see classes.root's width/maxWidth above) - explicit bug report that
            // squeezing these into narrower minmax() ranges broke the internal Grid layouts
            // (field rows, the Follow Up action-grid, etc.) that were all built assuming that
            // width. Default/fallback template - see tabsContentAreaWideMiddleShrink below for
            // the one case (Activity showing in the middle column) allowed to go narrower.
            gridTemplateColumns: '700px 700px 700px',
            gridTemplateRows: 'auto 1fr',
            gridTemplateAreas: '"detailsHead middleHead thirdHead" "details middle third"',
            columnGap: 24,
            // stretch (the grid default) rather than start - explicit request to make Activity
            // run the full column height instead of sitting at its own shorter content height
            // while its neighbouring columns run taller.
            alignItems: 'stretch',
        },
        // Explicit request: when a docked Quick View leaves less than the full 3×700px, shrink
        // only the middle column rather than dropping out of 3-column layout entirely - Details
        // and Follow-Up/Matter are excluded from this (stay fixed, via tabsContentAreaWide's own
        // template) since their own 2-up field Grids are the ones that broke at narrower widths
        // before (see that class's comment). Combined with tabsContentAreaWide via clsx
        // (overrides just its gridTemplateColumns).
        // Explicit bug report: this was only applied while isMiddleShowingActivity was true -
        // switching to Further Details (the middle slot's other occupant) reverted to the fixed
        // 700px-every-column template while the drawer's own outer maxWidth stayed clamped to
        // the smaller available width (the whole reason shrink mode exists), so the grid's own
        // fixed tracks overflowed straight past that clamp instead of fitting inside it - the
        // Follow-Up/Matter column visibly pushed outside the page. Always applying this (not
        // gated on which tab occupies the middle slot) means that slot's own content may get a
        // little tighter on Further Details than on Activity (its 2-up field Grid has less
        // room to work with down at ACTIVITY_COLUMN_MIN_WIDTH), but it stays inside the drawer's
        // own bounds either way, which the previous gating didn't guarantee.
        tabsContentAreaWideMiddleShrink: {
            gridTemplateColumns: `700px minmax(${ACTIVITY_COLUMN_MIN_WIDTH}px, 700px) 700px`,
        },
        // Explicit request: the new in-between 2-column layout (3 columns no longer fit, but
        // there's still room for 2) - Details/Activity/Further Details share the left column
        // (behind their own small tab switcher, see the leftHead heading below), Follow-Up/
        // Matter keep the exact same 'third' gridArea/content tabsContentAreaWide already uses,
        // unchanged. Left column always shrinkable (not gated on which of the 3 tabs is showing)
        // for the same reason tabsContentAreaWideMiddleShrink's own comment gives - avoids
        // reintroducing the overflow bug a tab-gated version of that fix already had.
        tabsContentAreaMedium: {
            flex: 1,
            display: 'grid',
            gridTemplateColumns: `minmax(${ACTIVITY_COLUMN_MIN_WIDTH}px, 700px) 700px`,
            gridTemplateRows: 'auto 1fr',
            gridTemplateAreas: '"leftHead thirdHead" "left third"',
            columnGap: 24,
            alignItems: 'stretch',
        },
        // A per-column heading for wide layout (Details / Activity-or-Further-Details /
        // Follow-Up-or-Matter) - explicit request, styled to look like classes.tabItem's own
        // selected state for visual consistency with the real tab bar, but without that bar's
        // own position:sticky/negative-margin (built for one full-width bar, not 3 of these
        // side by side in a grid row).
        columnHeadingBar: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: 44,
            borderBottom: `1px solid ${mainTheme.CommonColor.LightGrey2}`,
            marginBottom: 10,
        },
        // The Expand/Collapse All toggle next to the Details column heading - explicit request:
        // same text+icon Button style as the /admin page's own "Expand all"/"Collapse all"
        // toggle (AdminDashboard.tsx), not an icon-only button.
        columnHeadingAction: {
            marginRight: 6,
            color: theme.palette.text.secondary,
        },
        // The inner <Tabs> each column heading renders (even Details/Follow-Up-or-Matter, which
        // have nothing to switch between) - explicit bug report/screenshot: a plain styled
        // <span> here read as a disconnected underlined link, visually inconsistent with the
        // real Activity/Further Details <Tabs> next to it. Rendering every heading as a real
        // (if single-tab) MUI Tabs/Tab instead gives all 3 the exact same tabItem padding/
        // hover/selected treatment, no separate span styling needed.
        columnHeadingTabs: {
            minHeight: 44,
            '& .MuiTabs-flexContainer': {
                gap: 2,
                height: '100%',
            },
            '& .MuiTabs-indicator': {
                display: 'none',
            },
        },
        // Explicit request: replaces the Follow Up panel (which otherwise just renders as a
        // greyed-out, inert-looking accordion via its own disabled prop - see the Accordion
        // just above in activityTab()) with an explanation of why, whenever canFollowUp is
        // false. Wide layout only - narrow layout's existing Activity tab is unchanged.
        followUpUnavailable: {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            padding: '32px 24px',
            gap: 12,
        },
        followUpUnavailableIcon: {
            width: 40,
            height: 40,
            borderRadius: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            // Same blue the Follow Up panel's own header icon uses (sectionIconChipBlue above) -
            // this message stands in for that panel, so it keeps its colour.
            backgroundColor: 'var(--rv-accent-blue-bg)',
            color: 'var(--rv-accent-blue)',
            '& svg': {
                fontSize: 22,
            },
        },
        // not-proceeding variant: grey, like the Not Proceeding entry in the activity timeline
        followUpUnavailableIconMuted: {
            backgroundColor: 'var(--rv-accent-grey-bg)',
            color: 'var(--rv-accent-grey)',
        },
        followUpUnavailableText: {
            fontSize: 13,
            color: theme.palette.text.secondary,
            maxWidth: 260,
        },
        // Explicit request: icons on the header overflow menu's items (History Report / Delete).
        overflowMenuIcon: {
            minWidth: 34,
            color: 'inherit',
        },
        overflowMenuDeleteItem: {
            color: theme.palette.error.main,
        },
        followUpUnavailableTitle: {
            fontSize: 14,
            fontWeight: 600,
            color: theme.palette.text.primary,
        },
        // Explicit request (reference: Followup.png) - "What happened"/"What's next" section
        // headings inside the Follow Up panel.
        followUpSectionHeading: {
            fontSize: 14,
            fontWeight: 700,
            color: theme.palette.text.primary,
        },
        // Explicit bug report: "What's next" sat flush against the divider above it - this
        // modifier (combined with followUpSectionHeading, not replacing it) only applies to
        // that one, not "What happened" above, which has nothing above it to clear.
        followUpSectionHeadingSpaced: {
            marginTop: 16,
        },
        followUpSectionSubtext: {
            fontSize: 12.5,
            color: theme.palette.text.secondary,
            marginTop: 2,
            marginBottom: 10,
        },
        // Explicit bug report: no visible line separating the Follow Up header (icon/label/
        // assigned-to row) from the body below it - same border-bottom idea as the Activity
        // cards' own header/body divider (HistoryActivity.tsx's cardHeaderRowWithBody).
        // expansionPanelSummary itself is shared by every Accordion panel in this drawer, so
        // this is a separate modifier combined alongside it, scoped to just this one panel.
        // Explicit bug report/correction: an earlier version also added paddingBottom here,
        // making this one panel's header taller than every other Accordion panel's own
        // (shared expansionPanelSummary-only) header - border only, no extra padding, keeps
        // the height identical to every other panel (e.g. the Meeting/Assignment panels).
        followUpSummaryWithBorder: {
            borderBottom: `1px solid ${mainTheme.CommonColor.LightGrey2}`,
        },
        // Explicit request: the Accordion summary's existing "Assigned to X, due by Y" text,
        // restyled so the name reads bold/primary against a quieter secondary-coloured
        // sentence, matching the reference's "Assigned to **Name**" treatment - same
        // assignedToName/followUpDueDate fields and format, no logic change.
        followUpAssignedToText: {
            fontSize: 13,
            color: theme.palette.text.secondary,
            '& strong': {
                color: theme.palette.text.primary,
                fontWeight: 700,
            },
        },
        // Explicit request: wide layout's Follow Up panel saves/cancels independently of the
        // Details column's own Save button (see onSaveFollowUp/onCancelFollowUp) - narrow
        // layout keeps sharing the single global Save button, unchanged, so this row only
        // renders in wide layout.
        // Explicit request: a lightly-shaded footer bar so it stands out from the fields above
        // it, matching the Follow Up panel's own header accent (var(--rv-accent-blue-bg), the
        // same token its icon chip/label already use - see sectionIconChipBlue/sectionLabelBlue)
        // rather than a new colour. Bleeds to the panel's full width/bottom edge (negative
        // margin matching AccordionDetails' own 16px side / this panel's 15px bottom padding,
        // own padding restoring the buttons' inset) with matching bottom corner radius so it
        // reads as a proper footer bar, not an inset box floating inside the panel.
        followUpPanelActions: {
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            margin: '16px -16px -15px',
            padding: '12px 16px',
            borderTop: `1px solid ${mainTheme.CommonColor.LightGrey2}`,
            backgroundColor: 'var(--rv-accent-blue-bg)',
            borderBottomLeftRadius: 4,
            borderBottomRightRadius: 4,
        },
        labelWithIcon: {
            display: 'flex',
            alignItems: 'center',
            '& svg': {
                marginRight: 5,
            },
            '& a': {
                color: 'inherit'
            }
        },
        internalNotes: {
            // Moved into the header panel's identity column, under the phone/mobile row -
            // explicit request. Explicit follow-up request: this is the most useful context in
            // the header (e.g. a caller's callback-time preference), so it gets its own subtle
            // highlighted line instead of blending in as plain italic grey text.
            // Explicit request: no leading icon, and a long note must stay inside the identity column
            // (it used to run on one unbroken line underneath the Next Follow-up widget) - it wraps
            // and is clamped to 2 lines instead; the full text is in the tooltip.
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            marginTop: 0,
            marginBottom: 0,
            padding: '3px 8px',
            borderRadius: 6,
            fontSize: '12px',
            fontWeight: 600,
            lineHeight: 1.4,
            color: 'var(--rv-accent-blue)',
            backgroundColor: 'var(--rv-accent-blue-bg)',
            width: 'fit-content',
            maxWidth: '100%',
            overflow: 'hidden',
            overflowWrap: 'anywhere',
        },
        divider: {
            // marginTop: 10,
            width: '100%',
        },
        // Explicit bug report: switching the Follow Up Action radio (followUpSystemAction)
        // swapped this whole field set in/out instantly. Applied directly to the branch's own
        // top-level elements (never a wrapping div/AnimatedItem) - these sit as direct children
        // of a MUI Grid container, and an intervening wrapper breaks its spacing negative-margin
        // pairing with its item children. Keyed per systemAction (see each usage) so React
        // remounts the element on every switch, replaying this same fade/rise AnimatedItem
        // itself uses elsewhere in this drawer.
        followUpActionFieldsFade: {
            animation: '$followUpActionFieldsFadeIn 0.15s ease both',
            '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
            },
        },
        '@keyframes followUpActionFieldsFadeIn': {
            from: { opacity: 0, transform: 'translateY(10px)' },
            to: { opacity: 1, transform: 'translateY(0)' },
        },
        dropZoneFont: {
            '& .MuiDropzoneArea-text': {
                fontSize: '1em',
            }
        },
        checkedIcon: {
            color: mainTheme.TemplateColor.Primary,
        },
        createClientButton: {
            margin: '0 10px 10px 10px',
            flex: 1,
        },
        // Explicit request: the Create Matter / Mark as Created / Re-Open buttons read as their own footer
        // (divider above, a little air) instead of floating at the bottom of the timeline.
        customGroupButton: {
            display: 'flex',
            alignItems: 'center',
            margin: '0 -10px',
            paddingTop: 12,
            borderTop: '1px solid rgba(0,0,0,0.08)',
        },
        // Explicit bug report follow-up: '& > p' used to space phone/mobile apart, but the new
        // ContactLine component (below) renders a div, not a p - gap works regardless of the
        // child element type, so it covers both ContactLine and AddContactFieldLink.
        contactsContainer: {
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '4px 10px',
            '& > *': { flexShrink: 0 },
        },
        expandDrawer: {
            position: 'absolute',
            top: '5px',
            right: '10px',
            '& .MuiSvgIcon-root': {
                width: '20px',
                height: '20px',
            }
        },
        dropzoneClass: {
            paddingBottom: '15px',
            minHeight: 0,
        },
        checkoxContainer: {
            display: 'flex',
            alignItems: 'flex-start'
        },
        checkbboxWrapper: {
            flex: 1,
            display: 'flex',
            flexFlow: 'row wrap',
            padding: '0 8px',
            justifyContent: 'flex-start',
        },
        // Explicit bug report: checkbboxWrapper itself has no gap - it never needed one, since
        // every other row using this class is a row of FormControlLabel+Checkbox pairs whose
        // own default MUI margins provided the spacing. The bare Chips (ToggleChip) this one
        // row now holds have no such margin, so they sat flush against each other - a modifier
        // alongside the shared class (not a change to it) so the other checkbboxWrapper row
        // (still plain checkboxes, line ~7809) keeps its own existing spacing unchanged.
        checkChipWrapperGap: {
            gap: 8,
        },
        margin: {
            margin: theme.spacing(1),
        },
        referrerFieldWrapepr: {
            display: 'flex',
            alignItems: 'center',
            '& .field': {
                flex: 1,
            },
            '& .switcher': {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }
        },
        toggleFieldWrapepr: {
            display: 'flex',
            alignItems: 'flex-start',
            flex: 1,
            '& .field': {
                flex: 1,
            },
            '& .switcher': {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }
        },
        // Explicit request: Last Name's own switch-to-existing-contact button, as a 30px end
        // adornment inside the field rather than the toggleFieldWrapepr column it used to sit
        // in (see the Contact Details panel's lastName Field).
        nameSwitchAdornmentButton: {
            width: 30,
            height: 30,
        },
        formControlLabel: {
            marginLeft: 0,
            marginRight: 0,
        },
        // Explicit request: Invitee's Start/End and the All Day checkbox all sit on one row -
        // gap added here now that this wraps 3 items (previously just End+All Day, which relied
        // on the fields' own spacing; Start is a genuinely new third child).
        startTimeWrapper: {
            display: 'flex',
            flex: 1,
            alignItems: 'center',
            gap: 8,
        },
        textFieldWrapper: {
            flex: 1,
        },
        staffWrapper: {
            fontSize: 14,
            color: mainTheme.TemplateColor.Primary,
            padding: '0 5px',
        },
        // Explicit request: same small-label pattern TaskFlow's New Task dialog uses for its
        // own inline notes (10px radius, bordered, tinted) instead of the old flat
        // background-colour-only highlight with no radius/icon.
        noEmailMeetingWrapper: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: 6,
            fontSize: 12.5,
            lineHeight: 1.4,
            color: 'var(--rv-accent-orange)',
            backgroundColor: 'var(--rv-accent-orange-bg)',
            border: '1px solid rgba(224,90,0,0.25)',
            borderRadius: 10,
            padding: '8px 10px',
            '& svg': {
                fontSize: 16,
                flexShrink: 0,
                marginTop: 1,
            },
        },
        // Explicit request: a short note under the outcome picker once "Proceeding"/"Not
        // Proceeding" is chosen, confirming what that choice means - same small-label shape as
        // noEmailMeetingWrapper above, tinted to match whichever outcome was picked
        // (OutcomeSegmentedControl's own green/orange selected-state colours).
        // Explicit request: the outcome tabs and the Auto Follow Up bar share one row; it wraps the bar under the tabs on a narrow panel.
        outcomeRow: {
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: theme.spacing(1.25, 1.75),
        },
        outcomeNotice: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: 6,
            fontSize: 12.5,
            lineHeight: 1.4,
            borderRadius: 10,
            padding: '8px 10px',
            border: '1px solid transparent',
            '& svg': {
                fontSize: 16,
                flexShrink: 0,
                marginTop: 1,
            },
        },
        outcomeNoticeGreen: {
            color: 'var(--rv-accent-green)',
            backgroundColor: 'var(--rv-accent-green-bg)',
            borderColor: 'rgba(30,123,52,0.25)',
        },
        outcomeNoticeOrange: {
            color: 'var(--rv-accent-orange)',
            backgroundColor: 'var(--rv-accent-orange-bg)',
            borderColor: 'rgba(224,90,0,0.25)',
        },
        mainControlWrapper: {
            display: 'flex',
            justifyContent: 'space-between',
        },
        // Explicit bug report: this sat the Reminder at the bottom of its cell, so when the Follow Up Due beside it showed its "required" message
        // (making that cell taller) the Reminder was pushed down. Both fields are the same height, so top-aligned they stay level.
        selector: {
            height: '100%',
            display: 'flex',
            alignItems: 'flex-start'
        },
        fieldAndCheckbox: {
            display: 'flex',
            alignItems: 'center',
            '& .MuiFormControl-root': {
                flex: 1
            }
        },
        ratingWrapper: {
            display: 'flex',
            alignItems: 'center',
            // justifyContent: 'space-between',
            margin: '0 -10px',
            '& > legend': {
                padding: '0 10px',
            },
            '& > span': {
                padding: '0 10px',
            }
        },
        AccordionSummary: {
            '& .MuiAccordionSummary-content': {
                justifyContent: 'space-between',
            },
        },
        headingIcon: {
            marginRight: '10px',
        },
        headingIconContact: {
            color: '#1667C7',
        },
        headingIconEnquiry: {
            color: '#E05A00',
        },
        headingIconAssignment: {
            color: '#1E7B34',
        },
        sectionHeaderRow: {
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
        },
        sectionIconChip: {
            width: 28,
            height: 28,
            minWidth: 28,
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
        },
        sectionIconChipBlue: {
            backgroundColor: 'var(--rv-accent-blue-bg)',
            color: 'var(--rv-accent-blue)',
        },
        sectionIconChipOrange: {
            backgroundColor: 'var(--rv-accent-orange-bg)',
            color: 'var(--rv-accent-orange)',
        },
        sectionIconChipGreen: {
            backgroundColor: 'var(--rv-accent-green-bg)',
            color: 'var(--rv-accent-green)',
        },
        sectionIconChipGrey: {
            backgroundColor: 'var(--rv-accent-grey-bg)',
            color: 'var(--rv-accent-grey)',
        },
        sectionIconChipPurple: {
            backgroundColor: 'var(--rv-accent-purple-bg)',
            color: 'var(--rv-accent-purple)',
        },
        sectionIconChipTeal: {
            backgroundColor: 'var(--rv-accent-teal-bg)',
            color: 'var(--rv-accent-teal)',
        },
        sectionIconChipRed: {
            backgroundColor: 'var(--rv-accent-red-bg)',
            color: 'var(--rv-accent-red)',
        },
        sectionIconChipIndigo: {
            backgroundColor: 'var(--rv-accent-indigo-bg)',
            color: 'var(--rv-accent-indigo)',
        },
        sectionLabelBlue: {
            color: 'var(--rv-accent-blue)',
        },
        sectionLabelOrange: {
            color: 'var(--rv-accent-orange)',
        },
        sectionLabelGreen: {
            color: 'var(--rv-accent-green)',
        },
        sectionLabelGrey: {
            color: 'var(--rv-accent-grey)',
        },
        sectionLabelPurple: {
            color: 'var(--rv-accent-purple)',
        },
        sectionLabelTeal: {
            color: 'var(--rv-accent-teal)',
        },
        sectionLabelRed: {
            color: 'var(--rv-accent-red)',
        },
        sectionLabelIndigo: {
            color: 'var(--rv-accent-indigo)',
        },
        sectionLabelText: {
            textTransform: 'uppercase',
            fontSize: '11px',
            fontWeight: 500,
            letterSpacing: '1px',
        },
        displayFlex: {
            display: 'flex'
        },
        // ── Matter tab financials - modernised from the old fixed 2-column stack of plain
        // Cards into a reflowing tile grid with an icon chip + short description per figure,
        // matching the treatment already used for RedView Mobile V2's own Matter Financials
        // tab (MatterFinancialsTab.tsx) - explicit request to bring the two in line.
        card: {
            display: 'flex',
            flexFlow: 'column',
            width: '100%',
            height: '100%',
            boxSizing: 'border-box',
            background: '#fff',
            boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
            border: '1px solid rgba(0,0,0,0.06)',
            borderRadius: '8px',
            padding: theme.spacing(2),
        },
        cardWrapper: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: theme.spacing(2),
        },
        financialTileLabel: {
            fontSize: '12px',
            fontWeight: 600,
            color: mainTheme.TextColor.Secondary,
            marginBottom: 4,
        },
        financialTileValue: {
            fontSize: '20px',
            fontWeight: 700,
            color: mainTheme.TextColor.Primary,
            lineHeight: 1.3,
        },
        financialTileSubheading: {
            fontSize: '11px',
            color: mainTheme.TextColor.Tertiary,
            marginTop: 4,
        },
        financialFooter: {
            marginTop: theme.spacing(2),
        },
        financialFooterRow: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: theme.spacing(1.25, 0),
            fontSize: '13px',
            borderTop: '1px solid rgba(0,0,0,0.08)',
        },
        financialFooterLabel: {
            color: mainTheme.TextColor.Secondary,
        },
        financialFooterValue: {
            color: mainTheme.TextColor.Primary,
            fontWeight: 600,
        },
        matterSelectorWrapper: {
            display: 'flex',
            alignItems: 'center',
            '& > div': {
                flex: 1
            }
        },
        flex2: {
            flex: 2
        },
        notes: {
            backgroundColor: 'rgba(243, 243, 158, 0.29)',
            padding: '10px',
        },
        readRatingWrapper: {
            display: 'flex',
            alignItems: 'center',
            margin: '0 -10px',
            '& > div': {
                padding: '0 10px'
            }
        },
        truncateText: {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '30rem'
        },
        associatedWarning: {
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
        },
        // Same layout as associatedWarning, applied to AnimatedItem's own root instead - see
        // that call site's own comment for why the Popper sits outside this rather than
        // nested inside it.
        associatedWarningInline: {
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
        },
        associatedColor: {
            color: 'orange'
        },
        // The "a contact or enquiry with this name already exists" notice: a soft orange strip with the message in the same quiet 13px text as the Follow Up
        // panel's other labels (the info icon that opens the matches sits at its end).
        // Flashes stronger orange as it arrives, and again whenever a new check finds different matches (the notice is remounted then - see ContactDetailsPanel).
        '@keyframes duplicateNoticeFlash': {
            '0%': { backgroundColor: 'rgba(224, 90, 0, 0.32)' },
            '100%': { backgroundColor: 'rgba(224, 90, 0, 0.12)' },
        },
        duplicateNotice: {
            width: '100%',
            boxSizing: 'border-box',
            gap: 8,
            padding: '4px 6px 4px 12px',
            borderRadius: 8,
            backgroundColor: 'var(--rv-accent-orange-bg)',
            animation: '$duplicateNoticeFlash 0.9s ease-out',
            '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
            },
        },
        duplicateNoticeText: {
            flex: 1,
            fontSize: 13,
            color: theme.palette.text.primary,
        },
        duplicateNoticeIcon: {
            color: 'var(--rv-accent-orange)',
        },
        showContactWrapper: {
            padding: '10px',
            display: 'flex',
            flexFlow: 'column',
            gap: '5px',
            maxHeight: '750px',
            overflowY: 'auto',
            // whiteSpace: 'nowrap',
        },
        reminderSelectorInput: {
            paddingRight: '30px',
        },
        // Transparent: the duplicate-names card inside has its own white, rounded background, so a white box here showed as square corners behind it.
        popperWrapper: {
            backgroundColor: 'transparent',
            color: 'rgba(0, 0, 0, 0.87)',
            fontSize: 11,
            zIndex: 1300,
            padding: '15px',
        },
        accordionRoot: {
            margin: '5px 0 !important',
        }
    })
);
